import { createStyles, makeStyles, Theme } from '@material-ui/core';
import classNames from 'classnames';
import React from 'react';

import { PublishStatus, QuestionStatus } from '../../../../graphql/gen/types';
import { colorPalette, spacings, TextSizesEnum } from '../../../../theme';
import { PlexTypographyEllipsed } from '../../../shared/primitives/PlexTypographyEllipsed';
import { PlexName } from '../../common/PlexName';
import { PostDate } from '../../common/PostDate';
import { PostVisibility } from '../../common/PostVisibility';
import { useDialogState } from '../../dialog/useDialogState';
import { TopBar } from '../DetailsTopBar';

type Props = {
  className?: string;
  title: string;
  plexId: string;
  plexName: string;
  plexColor: string | null | undefined;
  plexPublishStatus: PublishStatus | QuestionStatus;
  publishedDate: number | string;
};

export function ItemAnalyticsTopbar(props: Props): React.ReactElement {
  const { title, plexId, plexName, plexColor, plexPublishStatus, publishedDate, className } = props;
  const classes = useStyles();
  const [dialogState, setDialogState] = useDialogState();
  const handleCloseDetailedView = (): void => {
    switch (dialogState?.type) {
      case 'fact_analytics_from_fact':
      case 'post_analytics_from_post': {
        setDialogState(null);
        break;
      }
      case 'fact_analytics_from_plex':
      case 'post_analytics_from_plex': {
        setDialogState({ type: 'plex_analytics', plexId });
        break;
      }
      default: {
        setDialogState(null);
        break;
      }
    }
  };
  return (
    <TopBar onClose={handleCloseDetailedView}>
      <div className={classNames(classes.root, className)}>
        <div className={classes.headerLeft}>
          <PlexName name={plexName} color={plexColor} />
          <PlexTypographyEllipsed
            className={classes.title}
            text={title}
            tooltip={title}
            size={TextSizesEnum.NormalPlus}
          />
        </div>
        <div className={classes.headerRight}>
          <PostDate className={classes.postDate} date={new Date(publishedDate)} />
          <div className={classes.publishStatus}>
            <PostVisibility plexPublishStatus={plexPublishStatus} plexName={plexName} />
          </div>
        </div>
      </div>
    </TopBar>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingLeft: spacings.xlarge,
      paddingTop: spacings.normal,
      paddingBottom: spacings.normal,

      flexDirection: 'row',
      paddingRight: spacings.normal,
      overflow: 'hidden',
    },
    title: {
      marginLeft: spacings.normal,
      fontWeight: 'bold',
    },
    headerLeft: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      overflow: 'hidden',
    },
    headerRight: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    postDate: {
      wordBreak: 'unset',
      whiteSpace: 'nowrap',
    },
    publishStatus: {
      display: 'flex',
      color: colorPalette.purpleGray._500,
      fontSize: theme.typography.fontSize,
      marginLeft: spacings.small,
    },
  })
);
